import React, { useState, useEffect, useRef, useContext } from 'react';
import ListLoadError from '../../components/ListLoadError';
import EntityScopeNotice from '../../components/Invoicing/EntityScopeNotice';
import FinancialsEmptyState from '../../components/Invoicing/FinancialsEmptyState';
import { extractApiError } from '../../utils/errorMessages';
import { useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShoppingCart, faTruck, faPlus, faEye, faCheckCircle, faClock, faExclamationTriangle, faSync, faEdit, faTrash, faClipboardList, faProjectDiagram } from '@fortawesome/free-solid-svg-icons';
import Layout from '../../components/Layout';
import { useFlashMessage } from '../../components/FlashMessage/FlashMessageContext';
import HMSPageHeader from '../../components/ui/HMSPageHeader';
import Footer from '../../components/ui/Footer';
import invoicingService, {
  deliveryScopedListNotice, deliveryScopedEmptyNotice,
  deliveryScopedListHeading, DELIVERY_SCOPED_NOTICE_STYLE,
} from '../../services/invoicingService';
import { AuthContext } from '../../context/AuthContext';
import { hasFinancialAccess, isDeliveryScopedInvoiceReader } from '../../utils/financialAccess';
import { isRestrictedCustomerTrader } from '../../utils/customerTraderAccess';
import InvoiceAuditModal from '../../components/Invoicing/InvoiceAuditModal';
import InvoiceSearchFilter from '../../components/Invoicing/InvoiceSearchFilter';
import InvoiceRelationshipModal from '../../components/Invoicing/InvoiceRelationshipModal';
import './OutgoingInvoice.css';
import SortableTh from '../../components/shared/SortableTh';
import useTableSort from '../../components/shared/useTableSort';
import useSavedRowFlash from '../../components/shared/useSavedRowFlash';
import SavedRowBanner from '../../components/shared/SavedRowBanner';
import { PURCHASE_ORDER_COLUMNS } from './listColumns';

/* What this screen actually shows. The delivery-scoped banner used to be
 * hard-coded "Showing your deliveries' invoices." on all four list screens,
 * so an operator on this one was told the wrong noun about the very list they
 * were being asked to trust. */
const SCOPED_DOCUMENT_PLURAL = 'purchase orders';

/**
 * Purchase Order List Page
 * 
 * Features:
 * - List all purchase orders
 * - Filter by status, supplier
 * - Search by PO number or supplier name
 * - Create new purchase order
 * - Track delivery status
 * - Mark as received
 */


const PurchaseOrderList = () => {
  const history = useHistory();
  const { showSuccess, showError } = useFlashMessage();
  // WHICH READER IS THIS? See IncomingInvoiceList for the full note — the same
  // two readers share all four invoice lists since 2026-09-07. A finance user
  // reads their whole entity book; a trade-ops operator reads only the documents
  // raised against deliveries they can already open, because
  // invoicing.delivery_scoped_access clamps the rows. This flag keeps the SCREEN
  // honest about that: anything below that the server would refuse is hidden
  // rather than left to fail into an empty panel.
  const { user } = useContext(AuthContext);
  const scopedReader = isDeliveryScopedInvoiceReader(user);
  // A customer trader is an external counterparty carrying has_bunkers_access,
  // so the route lets them in and the server then refuses them. Refuse here too
  // rather than ask on their behalf and draw a bare table.
  const barredTrader = Boolean(user) && isRestrictedCustomerTrader(user) && !hasFinancialAccess(user);
  const [orders, setOrders] = useState([]);
  // Column sorting — comparators live in utils/tableSort, column kinds in
  // ./listColumns, so all six invoicing lists sort by the same rules.
  const { sort, onSort, applySort } = useTableSort(PURCHASE_ORDER_COLUMNS);
  const [loading, setLoading] = useState(true);
  // Refusal or failure text for the banner. Without it a 403 renders as
  // an empty table, which reads as "you have none" rather than "we could
  // not ask".
  const [loadError, setLoadError] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownRef = useRef(null);
  
  // Search and filter state
  const [searchResults, setSearchResults] = useState([]);
  const [activeFilters, setActiveFilters] = useState({});
  const [isSearching, setIsSearching] = useState(false);
  const [isSearchMode, setIsSearchMode] = useState(false);
  
  // Audit modal state
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [selectedOrderForAudit, setSelectedOrderForAudit] = useState(null);
  
  // Relationship modal state
  const [showRelationshipModal, setShowRelationshipModal] = useState(false);
  const [selectedOrderForRelationship, setSelectedOrderForRelationship] = useState(null);
  
  // Component-level flag to prevent double load (resets on unmount)
  const hasLoadedRef = useRef(false);
  
  useEffect(() => {
    // `barredTrader` is false while the profile is still resolving, so re-run on
    // it: the refusal must land the moment we know who is asking.
    if (barredTrader) {
      setOrders([]);
      loadOrders();   // takes the refusal branch, makes no request
      return;
    }
    // Load orders only once on initial mount
    if (!isSearchMode && !hasLoadedRef.current) {
      hasLoadedRef.current = true;
      loadOrders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSearchMode, barredTrader]);
  
  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const loadOrders = async () => {
    // An external counterparty asking for our procurement book is not a load
    // failure to retry, it is an answer.
    if (barredTrader) {
      setLoading(false);
      setLoadError(
        'Purchase orders are internal to IMT. Your account is a customer trader '
        + 'account, which can see its own deliveries but not our procurement '
        + 'book. Contact your IMT representative if you need a copy.'
      );
      return;
    }
    try {
      setLoading(true);
      setLoadError(null);
      const data = await invoicingService.fetchPurchaseOrders({});
      setOrders(data.results || data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error loading purchase orders:', error);
      setLoadError(extractApiError(error, 'The purchase orders could not be loaded.'));
      setLoading(false);
    }
  };

  const handleSearchResults = (results) => {
    console.log('handleSearchResults called with', results.length, 'results');
    setSearchResults(results);
    
    // Enter search mode if we have results or active filters
    if (results.length > 0 || Object.keys(activeFilters).some(key => activeFilters[key])) {
      console.log('ENTERING SEARCH MODE - we have results or filters');
      setIsSearchMode(true);
    } else {
      console.log('NO RESULTS AND NO FILTERS - staying in search mode but showing empty');
      setIsSearchMode(true);
    }
  };

  const handleFiltersChange = (filters) => {
    setActiveFilters(filters);
    const hasFilters = Object.keys(filters).some(key => filters[key]);
    if (hasFilters) {
      setIsSearchMode(true);
    }
  };

  const handleSearchStateChange = (searching) => {
    setIsSearching(searching);
  };

  const handleClearSearch = () => {
    console.log('handleClearSearch() CALLED - EXITING SEARCH MODE AND LOADING ALL ITEMS');
    setIsSearchMode(false);
    setSearchResults([]);
    setActiveFilters({});
    loadOrders();
  };
  
  const handleMarkReceived = async (order) => {
    if (!window.confirm(
      `Mark purchase order ${order.po_number} as fully received?`
    )) {
      return;
    }
    
    try {
      await invoicingService.markPurchaseOrderReceived(order.id);
      showSuccess('Purchase order marked as received successfully!');
      loadOrders(); // Reload list
    } catch (error) {
      console.error('Error marking PO as received:', error);
      showError('Failed to mark purchase order as received. Please try again.');
    }
  };
  
  const getStatusIcon = (status) => {
    switch(status) {
      case 'DRAFT': return null;
      case 'PENDING_APPROVAL': return null;
      case 'APPROVED': return <FontAwesomeIcon icon={faCheckCircle} className="status-icon approved" />;
      case 'SENT': return <FontAwesomeIcon icon={faCheckCircle} className="status-icon sent" />;
      case 'CONFIRMED': return <FontAwesomeIcon icon={faCheckCircle} className="status-icon approved" />;
      case 'PARTIALLY_RECEIVED': return <FontAwesomeIcon icon={faTruck} className="status-icon pending" />;
      case 'FULLY_RECEIVED': return <FontAwesomeIcon icon={faCheckCircle} className="status-icon paid" />;
      case 'CLOSED': return <FontAwesomeIcon icon={faCheckCircle} className="status-icon paid" />;
      case 'CANCELLED': return <FontAwesomeIcon icon={faExclamationTriangle} className="status-icon cancelled" />;
      default: return null;
    }
  };
  
  const formatCurrency = (amount, currency) => {
    if (!amount || isNaN(amount)) return '0.00';
    return parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };
  
  const toggleDropdown = (orderId) => {
    setActiveDropdown(activeDropdown === orderId ? null : orderId);
  };
  
  const handleEdit = (order, e) => {
    e.stopPropagation();
    setActiveDropdown(null);
    history.push(`/invoicing/purchase-orders/edit/${order.id}`);
  };
  
  const handleDelete = async (order, e) => {
    e.stopPropagation();
    setActiveDropdown(null);
    
    if (window.confirm(`Are you sure you want to delete purchase order ${order.po_number}?`)) {
      try {
        await invoicingService.deletePurchaseOrder(order.id);
        loadOrders();
      } catch (error) {
        console.error('Error deleting purchase order:', error);
        showError('Failed to delete purchase order. Please try again.');
      }
    }
  };
  
  const handleViewGlobalAudit = () => {
    setSelectedOrderForAudit(null);
    setShowAuditModal(true);
  };
  
  const handleViewOrderAudit = (order, e) => {
    e.stopPropagation();
    setActiveDropdown(null);
    setSelectedOrderForAudit(order);
    setShowAuditModal(true);
  };
  
  const handleCloseAuditModal = () => {
    setShowAuditModal(false);
    setSelectedOrderForAudit(null);
  };
  
  const handleViewRelationships = (order, e) => {
    e.stopPropagation();
    setActiveDropdown(null);
    setSelectedOrderForRelationship(order);
    setShowRelationshipModal(true);
  };
  
  // Sorted wherever the rows came from — the loaded list OR the search
  // results — so the table never behaves differently in the two modes.
  const visibleRows = applySort(isSearchMode ? searchResults : orders);
  // Point the user at the row they just saved. The creators hand the id
  // over as route state; without it the toast names a row this table
  // gives no way to find (ordered by document date, not by recency).
  const { savedId, rowRef } = useSavedRowFlash({ sort, isSearchMode, activeFilters });
  const savedRowClass = (id) => (savedId !== null && String(id) === String(savedId) ? 'row-just-saved' : '');
  // The banner speaks only about a row that is actually on this page. If the
  // saved row was filtered out or fell beyond the fetched page, saying "see
  // below" would point at nothing -- the toast stands alone instead.
  // A DRAFT has no number yet, so the label falls back to generic wording
  // rather than printing 'undefined' at the user.
  const savedRow = savedId === null
    ? null
    : visibleRows.find(r => String(r.id) === String(savedId)) || null;
  const savedRowLabel = savedRow?.po_number || null;

  return (
    <Layout showLeftNavSidebar={true} showRightSidebar={false}>
      <div className="invoice-page-container purchase-order-screen">
        {/* Header */}
        {/* Back: the Invoicing dashboard aggregates the WHOLE book and stays
            finance-only, so sending an operator there walks them straight back
            into the permission bounce this change exists to remove. */}
        <HMSPageHeader
          onBack={() => history.push(scopedReader ? '/dashboard' : '/invoicing')}
          backLabel={scopedReader ? 'Back to Dashboard' : 'Back to Invoicing Dashboard'}
          title="Purchase Orders"
          kicker="Finance"
          subtitle="Procurement and supplier ordering"
          actions={
            <>
              {/* Audit Center reads `audit_logs`, a history of the whole book and
                  not in the delivery-scoped whitelist. For an operator it could
                  only 403, which the modal would draw as "no entries". */}
              {!scopedReader && (
                <button className="btn-secondary" onClick={handleViewGlobalAudit}>
                  <FontAwesomeIcon icon={faClipboardList} /> Audit Center
                </button>
              )}

              {!barredTrader && (
                <button className="btn-primary" onClick={() => history.push('/invoicing/purchase-orders/create')}>
                  <FontAwesomeIcon icon={faPlus} /> Create New PO
                </button>
              )}
            </>
          }
        />
        
        {/* The search bar drives the SAME `list` action with query params, which
            the scoped tier is allowed and the server clamps identically — so it
            works for an operator and stays mounted. Not rendered at all for a
            barred customer trader, who has no list to search — and not merely
            `hidden`, because `.search-filter-controls` sets `display: flex`, which
            wins over that attribute. */}
        {/* Search and Filter Controls */}
        {!barredTrader && (
        <div className="search-filter-controls">
          <div className="search-filter-wrapper">
            <InvoiceSearchFilter
              onSearchResults={handleSearchResults}
              onFiltersChange={handleFiltersChange}
              isSearching={isSearching}
              onSearchStateChange={handleSearchStateChange}
              onClearSearch={handleClearSearch}
              invoiceType="purchase-order"
            />
          </div>
          
          <button 
            className="reload-button"
            onClick={loadOrders}
            title="Reload orders"
          >
            <FontAwesomeIcon icon={faSync} />
          </button>
        </div>
        )}
        
        {/* PO List */}
        <div className="invoice-list-container">
          <ListLoadError message={loadError} onRetry={loadOrders} />
          {/* Says which entity's book this list is showing. Without it a
              narrowed list reads as "nothing has been raised" — the fault
              reported on 2026-09-03.
              An operator is narrowed by DELIVERY, not by entity (the scoped tier
              skips entity scoping on purpose), so the entity sentence would be a
              plain untruth for them. They get the rule that is actually in
              force. */}
          {scopedReader ? (
            <div role="status" style={DELIVERY_SCOPED_NOTICE_STYLE}>
              <strong style={{ marginRight: '0.4rem' }}>
                {deliveryScopedListHeading(SCOPED_DOCUMENT_PLURAL)}
              </strong>
              {deliveryScopedListNotice(SCOPED_DOCUMENT_PLURAL)}
            </div>
          ) : (
            <EntityScopeNotice emptyResult={!loading && visibleRows.length === 0} />
          )}
          <SavedRowBanner savedId={savedRow ? savedId : null} label={savedRowLabel} />
          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading purchase orders...</p>
            </div>
          ) : (
            <table className="invoice-list-table">
              <thead>
                <tr>
                  {PURCHASE_ORDER_COLUMNS.map((column) => (
                    <SortableTh key={column.key} column={column} sort={sort} onSort={onSort} />
                  ))}
                  {/* Actions holds buttons, not data — nothing to sort by. */}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="empty-state-cell">
                      <div className="empty-state-container">
                        <div className="empty-state-icon">
                          <FontAwesomeIcon icon={faShoppingCart} />
                        </div>
                        {/* FinancialsEmptyState explains an ENTITY narrowing. An
                            operator is narrowed by delivery instead, so it would name
                            the wrong reason in the largest text on the screen. */}
                        {scopedReader ? (
                          <div className="empty-state-text">
                            <h3>Nothing on your deliveries yet</h3>
                            <p>{deliveryScopedEmptyNotice(SCOPED_DOCUMENT_PLURAL)}</p>
                          </div>
                        ) : (
                          <FinancialsEmptyState
                            label="purchase orders"
                            heading="No Purchase Orders Yet"
                            blurb="Purchase orders will appear here"
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  visibleRows.map(order => (
                  <tr 
                    key={order.id}
                    ref={rowRef(order.id)}
                    className={savedRowClass(order.id)}
                    onClick={() => history.push(`/invoicing/purchase-orders/${order.id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td className="invoice-number-cell">{order.po_number || (order.status === 'DRAFT' ? 'DRAFT' : 'N/A')}</td>
                    <td>{order.hms_entity_name || 'N/A'}</td>
                    <td>{order.supplier_name || 'N/A'}</td>
                    <td>{order.po_date ? new Date(order.po_date).toLocaleDateString() : 'N/A'}</td>
                    <td>{order.expected_delivery_date ? new Date(order.expected_delivery_date).toLocaleDateString() : 'N/A'}</td>
                    <td>{order.currency || 'USD'}</td>
                    <td className="amount-cell">{formatCurrency(order.net_amount, order.currency)}</td>
                    <td className="amount-cell">{formatCurrency(order.vat_amount, order.currency)}</td>
                    <td className="amount-cell">{formatCurrency(order.gross_amount, order.currency)}</td>
                    <td className="status-cell">
                      {getStatusIcon(order.status)}
                      <span className={`status-badge ${order.status.toLowerCase()}`}>
                        {order.status_display || order.status}
                      </span>
                    </td>
                    <td className="actions-cell">
                      <div 
                        className={`action-dropdown ${activeDropdown === order.id ? 'dropdown-open' : ''}`}
                        onClick={(e) => e.stopPropagation()}
                        ref={activeDropdown === order.id ? dropdownRef : null}
                      >
                        <button 
                          className="gear-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleDropdown(order.id);
                          }}
                        >
                          Actions
                        </button>
                        {activeDropdown === order.id && (
                          <div className="dropdown-menu">
                            <button onClick={(e) => handleEdit(order, e)}>
                              <FontAwesomeIcon icon={faEdit} /> Edit
                            </button>
                            
                            {/* Relationships, mark-received, the audit log and
                                delete are all outside the delivery-scoped
                                whitelist, so for an operator each could only
                                403 — and three of the four render the refusal as
                                an empty panel or a failed alert. */}
                            {!scopedReader && (
                              <>
                                {/* View Relationships */}
                                <button onClick={(e) => handleViewRelationships(order, e)}>
                                  <FontAwesomeIcon icon={faProjectDiagram} /> View Relationships
                                </button>

                                {['APPROVED', 'SENT', 'CONFIRMED', 'PARTIALLY_RECEIVED'].includes(order.status) && (
                                  <button onClick={(e) => { e.stopPropagation(); setActiveDropdown(null); handleMarkReceived(order); }}>
                                    <FontAwesomeIcon icon={faTruck} /> Mark as Received
                                  </button>
                                )}
                                <button onClick={(e) => handleViewOrderAudit(order, e)}>
                                  <FontAwesomeIcon icon={faClipboardList} /> View Audit Log
                                </button>
                                <hr />
                                <button className="danger" onClick={(e) => handleDelete(order, e)}>
                                  <FontAwesomeIcon icon={faTrash} /> Delete
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
        
        {/* Footer */}
        <Footer />
      </div>
      
      {/* Audit Modal */}
      <InvoiceAuditModal
        isOpen={showAuditModal}
        onClose={handleCloseAuditModal}
        invoiceType="purchase-order"
        invoiceId={selectedOrderForAudit?.id || null}
        invoiceNumber={selectedOrderForAudit?.po_number || null}
      />
      
      {/* Relationship Modal */}
      <InvoiceRelationshipModal
        isOpen={showRelationshipModal}
        onClose={() => setShowRelationshipModal(false)}
        invoice={selectedOrderForRelationship}
        invoiceType="po"
      />
    </Layout>
  );
};

export default PurchaseOrderList;

